import ArtworkReactionsSummary from '@/views/hostGameSummary/artworkReactionsSummary/ArtworkReactionsSummary';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import PlayerCardsSummary, { GraphDataExtended } from './PlayersCardsSummary';
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import * as queries from '@/graphql/queries';
import { PlayerCardDrawings } from '@/API';
import { useAmplifyApiClient } from '@/hooks/AmplifyApiClientHooks';
import { useSessionId } from '@/hooks/HostSessionControlHooks';
import PlayerCardsSummaryShare from './PlayersCardsSummaryShare';
import { Stage } from 'konva/lib/Stage';
import AnimatedSummary from '../Animated/Backgrounds/AnimatedSummaryBackground';
import OldNavMenu from '../Navigation/OldNavMenu';
import OldLogoIcon from '../Icons/OldLogoIcon';

/**
 * Displays a thank you message with enhanced animation
 * Automatically transitions to cards screen
 */
const ThanksScreen = ({ onTransitionToCards }: { onTransitionToCards: () => void }) => {
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    let timer1: NodeJS.Timeout;
    let timer2: NodeJS.Timeout;

    if (animationStage === 0) {
      timer1 = setTimeout(() => {
        setAnimationStage(1);
      }, 2000);
    }

    if (animationStage === 1) {
      timer2 = setTimeout(() => {
        onTransitionToCards();
      }, 3000);
    }

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [animationStage, onTransitionToCards]);

  return (
    <motion.div
      className="flex flex-col items-center justify-center fixed inset-0"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{
        opacity: 1,
        scale: 1,
        transition: {
          duration: 0.6,
          type: 'spring',
          bounce: 0.4
        }
      }}
      exit={{
        opacity: 0,
        scale: 1.2,
        transition: {
          duration: 0.4
        }
      }}
    >
      <AnimatePresence mode="wait">
        {animationStage === 0 && (
          <motion.div
            key="thanks"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: {
                type: 'spring',
                stiffness: 100
              }
            }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0.3
              }
            }}
          >
            <motion.h1
              className="text-5xl font-extrabold text-[#20145F] text-center"
              initial={{ opacity: 0, y: -50 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  type: 'spring',
                  stiffness: 100
                }
              }}
            >
              Thank you for playing!
            </motion.h1>
            <motion.p
              className="mt-24 text-2xl font-bold text-[#20145F] text-center"
              initial={{ opacity: 0, y: -50 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: 0.2,
                  type: 'spring',
                  stiffness: 100
                }
              }}
            >
              We&apos;re almost done
            </motion.p>
          </motion.div>
        )}

        {animationStage === 1 && (
          <motion.div
            key="transition"
            initial={{ opacity: 0, y: -50 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                type: 'spring',
                stiffness: 100
              }
            }}
            className="text-4xl text-[#20145F] text-center"
          >
            Let&apos;s see what everyone has <br />
            created <span className="font-bold">together</span>!
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const HostGameSummary = () => {
  // States
  const [showCards, setShowCards] = useState(false);
  const [playerCards, setPlayerCards] = useState<PlayerCardDrawings[]>([]);
  const [graphData, setGraphData] = useState<GraphDataExtended | null>(null);

  // Hooks
  const client = useAmplifyApiClient();
  const sessionId = useSessionId();

  // Ref
  const canvas = useRef<Stage | null>(null);

  /**
   * Handles downloading the current drawing as PNG
   */
  const handleDownload = async () => {
    if (!canvas.current) return;

    await canvas.current.toBlob({
      callback: async (blob) => {
        if (!blob) return;

        const data = {
          files: [
            new File([blob], 'scribl-artifact.png', {
              type: blob.type
            })
          ]
        };

        // Check if on Anroid or iOS
        if (navigator.userAgent.match(/Android|iPhone|iPad|iPod/i)) {
          const shareData = {
            text: 'Check out my drawing!',
            files: [
              new File([blob], 'scribl-artifact.png', {
                type: blob.type
              })
            ]
          };

          await navigator.share(shareData);
          return;
        } else {
          const link = document.createElement('a');
          link.download = 'scribl-artifact.png';
          link.href = URL.createObjectURL(data.files[0]);
          link.click();
        }
      }
    });
  };

  /**
   * Fetches player drawings from the API
   */
  useEffect(() => {
    if (!sessionId) return;

    const fetchDrawings = async () => {
      try {
        const result = (await client.graphql({
          query: queries.playerCardListBySession,
          variables: { sessionId }
        })) as any;

        const cards: PlayerCardDrawings[] = result.data.playerCardListBySession ?? [];
        setPlayerCards(cards);
      } catch (error) {
        console.error('Error fetching drawings:', error);
      }
    };

    fetchDrawings();
  }, [sessionId, client]);

  return (
    <>
      <AnimatedSummary />

      <AnimatePresence>
        {!showCards ? (
          <ThanksScreen key="thanks" onTransitionToCards={() => setShowCards(true)} />
        ) : (
          <div className="fixed inset-0 w-full h-full bg-center bg-no-repeat bg-cover">
            <OldNavMenu includeRole={false} includeProfile={true} includeLogo={false} />
            <motion.div
              className="absolute inset-0 flex justify-center pb-96 pt-160"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: {
                  duration: 1,
                  delay: 0.5
                }
              }}
            >
              <PlayerCardsSummary playerCards={playerCards} onUpdateGraphData={setGraphData} />
              {graphData && <PlayerCardsSummaryShare graphData={graphData} ref={canvas} />}
            </motion.div>

            <div className="fixed transform -translate-y-1/2 top-96 w-full flex justify-center items-center">
              <OldLogoIcon className="w-full max-w-xs" />
            </div>

            <div className="fixed transform -translate-y-1/2 bottom-32 w-full">
              <div className="flex justify-center items-center gap-80">
                <ArtworkReactionsSummary />

                <div className="flex flex-col items-center justify-center gap-16 w-3xl">
                  <button onClick={handleDownload} className="btn btn-primary btn-lg">
                    Download
                    <FontAwesomeIcon icon={faDownload} className="ml-2" />
                  </button>
                  <Link to={`/dashboard`} className="text-pink-500">
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HostGameSummary;
